import React, { useMemo } from 'react';
import { useHolidayTheme } from '../../contexts/HolidayThemeContext';

interface Snowflake {
  id: number;
  x: string;
  size: string;
  opacity: string;
  duration: string;
  delay: string;
  drift: string;
  variant: 'dot' | 'star';
  spin: string;
  glyph: string;
}

// Denser field of small flakes for a heavier snowfall.
const FLAKE_COUNT = 120;
// Only a rare few flakes become large, decorative snowflakes — kept low so the
// big flakes aren't overwhelming.
const STAR_CHANCE = 0.02;
// Trailing U+FE0E (text variation selector) forces monochrome TEXT rendering, so
// these show as white glyphs instead of the platform's blue colour emoji — the
// snowflake char U+2744 (❄) otherwise renders as a coloured emoji on mobile.
const STAR_GLYPHS = ['❄\uFE0E', '❅\uFE0E', '❆\uFE0E'];

function buildFlakes(count: number): Snowflake[] {
  return Array.from({ length: count }, (_, id) => {
    const isStar = Math.random() < STAR_CHANCE;
    const size = isStar ? 12 + Math.random() * 10 : 2 + Math.random() * 4;
    // Big flakes drift down a little slower and more gracefully.
    const duration = isStar ? 16 + Math.random() * 12 : 9 + Math.random() * 12;
    const drift = (Math.random() - 0.5) * (isStar ? 120 : 80);
    const spin = ((Math.random() < 0.5 ? -1 : 1) * (180 + Math.random() * 360)).toFixed(0);
    return {
      id,
      x: `${(Math.random() * 100).toFixed(2)}%`,
      size: `${size.toFixed(1)}px`,
      opacity: (isStar ? 0.75 + Math.random() * 0.25 : 0.35 + Math.random() * 0.5).toFixed(2),
      duration: `${duration.toFixed(1)}s`,
      delay: `${(-Math.random() * duration).toFixed(1)}s`,
      drift: `${drift.toFixed(1)}px`,
      variant: isStar ? 'star' : 'dot',
      spin: `${spin}deg`,
      glyph: STAR_GLYPHS[Math.floor(Math.random() * STAR_GLYPHS.length)],
    };
  });
}

const HolidaySnowfall: React.FC = () => {
  const { snowEnabled } = useHolidayTheme();
  const flakes = useMemo(() => buildFlakes(FLAKE_COUNT), []);

  if (!snowEnabled) return null;

  return (
    <div className="holiday-snowfall" aria-hidden="true">
      {flakes.map((flake) => (
        <span
          key={flake.id}
          className={
            flake.variant === 'star'
              ? 'holiday-snowflake holiday-snowflake--star'
              : 'holiday-snowflake'
          }
          style={{
            ['--snow-x' as string]: flake.x,
            ['--snow-size' as string]: flake.size,
            ['--snow-opacity' as string]: flake.opacity,
            ['--snow-duration' as string]: flake.duration,
            ['--snow-delay' as string]: flake.delay,
            ['--snow-drift' as string]: flake.drift,
            ['--snow-spin' as string]: flake.spin,
          }}
        >
          {flake.variant === 'star' ? flake.glyph : null}
        </span>
      ))}
    </div>
  );
};

export default HolidaySnowfall;
